import React from 'react';
import i18n from '@/localisation/i18n';
import { DaybookType } from '../api/getDaybookList';

export type DaybookVisualConfig = {
  key: string;
  title: string;
  badgeClass: string;
  cardBgClass: string;
  cardBorderClass: string;
  glowClass: string;
  timeClass: string;
  accentGradient: string;
  icon: React.ReactNode;
  watermarkIcon: React.ReactNode;
};

export const getDaybookVisualConfig = (
  type?: DaybookType
): DaybookVisualConfig => {
  switch (type) {
    case 'dream':
      return {
        key: 'dream',
        title: i18n.t('daybook.type.dream'),
        badgeClass:
          'bg-indigo-500/20 text-indigo-200 border-indigo-400/40 shadow-[0_0_12px_rgba(99,102,241,0.25)]',
        cardBgClass:
          'bg-gradient-to-br from-[#1c1d3c]/90 via-[#18192d]/90 to-[#121323]/95',
        cardBorderClass:
          'border-indigo-500/30 hover:border-indigo-400/70 hover:shadow-[0_12px_32px_rgba(99,102,241,0.2)]',
        glowClass: 'bg-indigo-500/15',
        timeClass: 'text-indigo-200/70',
        accentGradient: 'from-indigo-500/30 via-indigo-400/10 to-transparent',
        icon: (
          <svg
            className="h-3.5 w-3.5 shrink-0 text-indigo-300"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" fill="currentColor" fillOpacity="0.3" />
            <path d="M19 3v4" />
            <path d="M21 5h-4" />
          </svg>
        ),
        watermarkIcon: (
          <svg
            className="h-28 w-28 text-indigo-400/5"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
          </svg>
        ),
      };

    case 'gratitude':
      return {
        key: 'gratitude',
        title: i18n.t('daybook.type.gratitude'),
        badgeClass:
          'bg-rose-500/20 text-rose-200 border-rose-400/40 shadow-[0_0_12px_rgba(244,63,94,0.25)]',
        cardBgClass:
          'bg-gradient-to-br from-[#301826]/90 via-[#231721]/90 to-[#181217]/95',
        cardBorderClass:
          'border-rose-500/30 hover:border-rose-400/70 hover:shadow-[0_12px_32px_rgba(244,63,94,0.2)]',
        glowClass: 'bg-rose-500/15',
        timeClass: 'text-rose-200/70',
        accentGradient: 'from-rose-500/30 via-rose-400/10 to-transparent',
        icon: (
          <svg
            className="h-3.5 w-3.5 shrink-0 text-rose-300"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path
              d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"
              fill="currentColor"
              fillOpacity="0.3"
            />
          </svg>
        ),
        watermarkIcon: (
          <svg
            className="h-28 w-28 text-rose-400/5"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
          </svg>
        ),
      };

    case 'standard':
    default:
      return {
        key: 'standard',
        title: i18n.t('daybook.type.standard'),
        badgeClass:
          'bg-teal-500/20 text-teal-200 border-teal-400/40 shadow-[0_0_12px_rgba(20,184,166,0.25)]',
        cardBgClass:
          'bg-gradient-to-br from-[#152525]/90 via-[#1b2225]/90 to-[#14181a]/95',
        cardBorderClass:
          'border-teal-500/30 hover:border-teal-400/70 hover:shadow-[0_12px_32px_rgba(20,184,166,0.2)]',
        glowClass: 'bg-teal-500/15',
        timeClass: 'text-teal-200/70',
        accentGradient: 'from-teal-500/30 via-teal-400/10 to-transparent',
        icon: (
          <svg
            className="h-3.5 w-3.5 shrink-0 text-teal-300"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1-2.5-2.5Z" />
            <path d="M6 6h10" />
            <path d="M6 10h10" />
            <path d="M6 14h6" />
          </svg>
        ),
        watermarkIcon: (
          <svg
            className="h-28 w-28 text-teal-400/5"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1-2.5-2.5Z" />
          </svg>
        ),
      };
  }
};
